import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  Typography,
  IconButton,
  Tab,
  Tabs,
  InputAdornment,
  styled,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffOutlinedIcon from "@mui/icons-material/MicOffOutlined";
import Send from "../../assets/images/Send.svg";

const ColorButton = styled(Button)(() => ({
  color: "black",
  backgroundColor: "#6D94A7",
  minWidth: "40px",
  height: "40px",
  padding: "0px",
  "&.Mui-disabled": {
    backgroundColor: "#E5E5E5",
    color: "black",
    border: "1px solid #E5E5E5",
  },
  "&:hover": {
    backgroundColor: "#396D87",
  },
}));

function DetailingPriorityTab({
  isPromptSubmitted,
  detailApiData,
  selectedProducts,
  setSelectedProducts,
  selectedKeyMessages,
  setSelectedKeyMessages,
  selectedKeyMessagesByProduct,
  setSelectedKeyMessagesByProduct,
  detailQuery,
  setDetailQuery,
  onSendPrompt,
  isFetchingDetail,
  detailError,
  isListening,
  onMicClick,
}) {
  const [activeProductTab, setActiveProductTab] = useState(0);

  const flattenSelectedKMs = useCallback((mapObj) => {
    const all = Object.values(mapObj || {}).flat();
    return Array.from(new Set(all));
  }, []);

  const productOptions = useMemo(
    () => detailApiData?.products || [],
    [detailApiData],
  );

  // Build a lookup of KM details by ID/Name
  const keyMessageDetailsFromApi = useMemo(() => {
    const map = {};
    for (const p of detailApiData?.products || []) {
      for (const km of p.keyMessages || []) {
        const id = km?.KeyMessageID || km?.KeyMessageName;
        if (!id) continue;
        map[id] = {
          title: km?.KeyMessageName || String(id),
          description: km?.KeyMessageDescription || "",
        };
      }
    }
    return map;
  }, [detailApiData]);

  useEffect(() => {
    // Keep active tab index valid when product selection changes
    setActiveProductTab((prev) => {
      const max = (selectedProducts || []).length - 1;
      if (max < 0) return 0;
      return prev > max ? 0 : prev;
    });
  }, [selectedProducts]);

  // Key message dropdown options are filtered by selected products
  const activeProductName =
    (selectedProducts || [])[activeProductTab] ||
    (selectedProducts || [])[0] ||
    "";

  const activeKeyMessages =
    selectedKeyMessagesByProduct[activeProductName] || [];
  const selectedMsgs = activeKeyMessages; // <-- use this for Message Details

  const keyMessageOptions = useMemo(() => {
    if (!detailApiData?.products?.length) return [];
    if (!activeProductName) return [];

    const productObj = detailApiData.products.find(
      (p) => p.name === activeProductName,
    );
    const kms = Array.isArray(productObj?.keyMessages)
      ? productObj.keyMessages
      : [];

    return kms
      .map((km) => {
        const value = km?.KeyMessageID || km?.KeyMessageName;
        if (!value) return null;
        return { value, label: km?.KeyMessageName || String(value) };
      })
      .filter(Boolean);
  }, [detailApiData, activeProductName]);

  // -------------------------
  // Handlers
  // -------------------------
  const handleProductsChange = (e) => {
    const vals = e.target.value || [];
    setSelectedProducts(vals);

    setSelectedKeyMessagesByProduct((prev) => {
      const next = { ...prev };

      // remove entries for unselected products
      Object.keys(next).forEach((p) => {
        if (!vals.includes(p)) delete next[p];
      });

      // ensure keys exist for selected products
      vals.forEach((p) => {
        if (!next[p]) next[p] = [];
      });

      // Backward compatibility: keep old array updated (flattened)
      setSelectedKeyMessages(flattenSelectedKMs(next));

      return next;
    });
  };

  const handleKeyMessagesChange = (e) => {
    const newVals = e.target.value || [];

    setSelectedKeyMessagesByProduct((prev) => {
      const next = { ...prev, [activeProductName]: newVals };

      // keep backward-compatible flattened list updated
      const flattened = Array.from(new Set(Object.values(next).flat()));
      setSelectedKeyMessages(flattened);

      return next;
    });
  };

  // ✅ Only "-" button: remove/de-select that key message
  const removeKeyMessageSelection = (msgId) => {
    setSelectedKeyMessagesByProduct((prev) => {
      const next = { ...prev };
      const current = next[activeProductName] || [];
      next[activeProductName] = current.filter((x) => x !== msgId);

      const flattened = Array.from(new Set(Object.values(next).flat()));
      setSelectedKeyMessages(flattened);

      return next;
    });
  };

  const removeProduct = (productName) => {
    setSelectedProducts((prev) =>
      (prev || []).filter((x) => x !== productName),
    );

    setSelectedKeyMessagesByProduct((prev) => {
      const next = { ...prev };
      delete next[productName];

      setSelectedKeyMessages(flattenSelectedKMs(next));
      return next;
    });
  };

  const productsMissingKMs = (selectedProducts || []).filter(
    (p) => (selectedKeyMessagesByProduct?.[p] || []).length === 0,
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Prompt input + Mic + Go */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box sx={{ mb: 0.5, fontSize: "12px", fontWeight: 600, color: "#333" }}>
          Describe the call (type or use mic)
        </Box>

        <TextField
          fullWidth
          value={detailQuery}
          onChange={(e) => setDetailQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSendPrompt();
            }
          }}
          placeholder="Enter text..."
          variant="outlined"
          className="calllog-prompt"
          multiline
          InputProps={{
            endAdornment: (
              <InputAdornment
                position="end"
                className="calllog-prompt-adornment"
              >
                <IconButton
                  onClick={onMicClick}
                  size="small"
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "6px",
                    color: "#0f5b8a",
                  }}
                >
                  {isListening ? (
                    <MicIcon fontSize="small" />
                  ) : (
                    <MicOffOutlinedIcon fontSize="small" />
                  )}
                </IconButton>

                <ColorButton
                  onClick={onSendPrompt}
                  disabled={!detailQuery.trim() || isFetchingDetail}
                >
                  {isFetchingDetail ? (
                    <CircularProgress size={18} sx={{ color: "black" }} />
                  ) : (
                    <img src={Send} alt="send" />
                  )}
                </ColorButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#f5f5f5",
              alignItems: "flex-start",
              paddingRight: "6px",
              "& fieldset": { borderColor: "#e0e0e0" },
            },
            "& textarea": {
              fontSize: "14px",
              lineHeight: "20px",
            },
            "& textarea::placeholder": {
              fontSize: "14px",
              opacity: 1,
            },
          }}
        />

        {detailError && (
          <Typography sx={{ color: "#d32f2f", fontSize: "12px" }}>
            {detailError}
          </Typography>
        )}
      </Box>

      <>
        {/* Products */}
        <Box>
          <Box sx={{ mb: 1, fontSize: "12px", fontWeight: 600, color: "#333" }}>
            Product Name(s)
          </Box>

          <TextField
            fullWidth
            select
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              value: selectedProducts || [],
              onChange: handleProductsChange,
              renderValue: (selected) => {
                const count = selected?.length || 0;
                if (count === 0) return "Select product(s)";
                return `${count} product(s) selected`;
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
                height: "40px",
                "& fieldset": { borderColor: "#e0e0e0" },
              },
              // optional: make placeholder look like placeholder
              "& .MuiSelect-select": {
                color: (selectedProducts || []).length ? "#000" : "#9e9e9e",
              },
            }}
          >
            {productOptions.map((p) => {
              const value = p?.name || "";
              const checked = (selectedProducts || []).includes(value);
              return (
                <MenuItem key={value} value={value}>
                  <Checkbox checked={checked} />
                  <ListItemText primary={value} />
                </MenuItem>
              );
            })}
          </TextField>

          {(selectedProducts || []).length > 0 && (
            <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
              {(selectedProducts || []).map((val) => (
                <Chip
                  key={val}
                  label={val}
                  onDelete={() => removeProduct(val)}
                  sx={{
                    backgroundColor: "#5f8aa5",
                    color: "white",
                    borderRadius: "100px",
                    "& .MuiChip-deleteIcon": { color: "white" },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* ✅ Product Tabs + Key Messages (driven by active product) */}
        {(selectedProducts || []).length > 0 && (
          <Box sx={{ background: "#f5f5f5", borderRadius: 2, p: 2 }}>
            {/* Product Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: "#eaeaea", mb: 2 }}>
              <Tabs
                value={activeProductTab}
                onChange={(_, v) => setActiveProductTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  minHeight: "36px",
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontSize: "14px",
                    minHeight: "36px",
                    px: 2,
                    color: "#333",
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: "#e56800",
                    fontWeight: 700,
                  },
                  "& .MuiTabs-indicator": { backgroundColor: "#e56800" },
                }}
              >
                {(selectedProducts || []).map((product) => (
                  <Tab key={product} label={product} />
                ))}
              </Tabs>
            </Box>

            {/* Key Message Name */}
            <Box>
              <Box
                sx={{
                  mb: 1,
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#333",
                }}
              >
                Key Message Name
              </Box>

              <TextField
                fullWidth
                select
                SelectProps={{
                  multiple: true,
                  displayEmpty: true,
                  value: activeKeyMessages || [],
                  onChange: handleKeyMessagesChange,
                  renderValue: (selected) => {
                    const count = selected?.length || 0;
                    if (count === 0) return "Select key message(s)";
                    return `${count} KM(s) selected`;
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    height: "40px",
                    "& fieldset": { borderColor: "#e0e0e0" },
                  },
                  "& .MuiSelect-select": {
                    color: (activeKeyMessages || []).length
                      ? "#000"
                      : "#9e9e9e",
                  },
                }}
              >
                {keyMessageOptions.map((km) => {
                  const checked = activeKeyMessages.includes(km.value);
                  return (
                    <MenuItem key={km.value} value={km.value}>
                      <Checkbox checked={checked} />
                      <ListItemText primary={km.label} />
                    </MenuItem>
                  );
                })}
              </TextField>

              {/* Chips */}
              {(activeKeyMessages || []).length > 0 && (
                <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
                  {(activeKeyMessages || []).map((val) => {
                    const label = keyMessageDetailsFromApi[val]?.title || val;
                    return (
                      <Chip
                        key={val}
                        label={label}
                        onDelete={() => removeKeyMessageSelection(val)}
                        sx={{
                          backgroundColor: "#5f8aa5",
                          color: "white",
                          borderRadius: "100px",
                          "& .MuiChip-deleteIcon": { color: "white" },
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* ✅ Message Details (Description shown by default + only "-" button) */}
            {selectedMsgs.length > 0 && (
              <Box
                sx={{
                  backgroundColor: "#fff",
                  border: "1px solid #e0e0e0",
                  borderRadius: 4,
                  p: 2,
                  mt: 2,
                }}
              >
                {selectedMsgs.map((msg) => (
                  <Box
                    key={msg}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mt: 2,
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        backgroundColor: "rgba(227, 227, 227, 0.7)",
                        borderRadius: "8px",
                        border: "1px solid #D1D1D1",
                        p: 2,
                      }}
                    >
                      <Typography
                        sx={{ fontWeight: 600, fontSize: "12px", mb: 0.5 }}
                      >
                        Key Message
                      </Typography>
                      <Typography sx={{ fontSize: "14px", mb: 2 }}>
                        {keyMessageDetailsFromApi[msg]?.title || msg}
                      </Typography>

                      <Typography
                        sx={{ fontWeight: 600, fontSize: "12px", mb: 0.5 }}
                      >
                        Description
                      </Typography>
                      <Typography sx={{ fontSize: "14px", color: "#555" }}>
                        {keyMessageDetailsFromApi[msg]?.description || ""}
                      </Typography>
                    </Box>

                    {/* Only "-" button */}
                    <Box
                      sx={{
                        width: 40,
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => removeKeyMessageSelection(msg)}
                        sx={{
                          backgroundColor: "#F33B28",
                          color: "white",
                          width: 20,
                          height: 20,
                          "&:hover": { backgroundColor: "#F33B28" },
                          fontSize: 14,
                        }}
                      >
                        −
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </>

      {productsMissingKMs.length > 0 && (
        <Box sx={{ mt: 1, color: "#d32f2f", fontSize: "12px" }}>
          Please select at least 1 key message for:{" "}
          {productsMissingKMs.join(", ")}
        </Box>
      )}
    </Box>
  );
}

export default DetailingPriorityTab;
